import { useRef, useCallback } from 'react';
import { startSession, sendMessageStream } from '../services/api';

function useChatSession({
  profileIdx,
  customProfile,
  isOarsMode,
  isStrategiesMode,
  isClientMode,
  sessionActive,
  sessionId,
  conversationLength,
  onConversationStart,
  onSessionRegistered,
  onSystemMessagesUpdate,
  onTurnClassification,
  onTurnGuidance,
  onTurnGuidanceError,
  onRevealedItemsUpdate,
  onStageMetricsUpdate,
  onSessionEnd,
  notify,
  setLoading,
  setConversation,
  setSessionId,
  setSessionEnded,
  setEndReason,
}) {
  const initializingRef = useRef(false);

  const resolveClientType = useCallback(() => 'Profile-Reveal', []);

  const initSession = useCallback(async () => {
    if (initializingRef.current) return;
    if (isOarsMode || isStrategiesMode) {
      initializingRef.current = false;
      return;
    }

    initializingRef.current = true;

    if (isClientMode) {
      initializingRef.current = false;
      return;
    }

    try {
      setLoading(true);

      const clientType = resolveClientType();
      const data = await startSession(
        profileIdx === -1 ? -1 : profileIdx,
        clientType,
        false,
        profileIdx === -1 ? customProfile : null
      );

      setSessionId(data.session_id);
      onSessionRegistered?.(data.session_id, {
        clientVignette: data.client_vignette || null,
      });
      if (data.initial_message) {
        setConversation([data.initial_message]);
      }
    } catch (err) {
      console.error('Failed to start session:', err);
      const apiMsg = err?.response?.data?.error;
      notify(apiMsg || 'Failed to start session. Please check backend connection.');
    } finally {
      setLoading(false);
      initializingRef.current = false;
    }
  }, [
    customProfile,
    isClientMode,
    isOarsMode,
    isStrategiesMode,
    notify,
    onSessionRegistered,
    profileIdx,
    resolveClientType,
    setConversation,
    setLoading,
    setSessionId,
  ]);

  const handleFreeformSend = useCallback(async (messageToSend) => {
    if (onConversationStart && conversationLength === 0) {
      onConversationStart();
    }

    setConversation((prev) => [
      ...prev,
      { speaker: 'counsellor', text: messageToSend },
      { speaker: 'client', text: '', isStreaming: true },
    ]);

    const { promise } = sendMessageStream(
      messageToSend,
      (token) => {
        setConversation((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = { ...last, text: last.text + token };
          return updated;
        });
      },
      (data) => {
        setConversation((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = { speaker: 'client', text: last.text };
          return updated;
        });

        if (onSystemMessagesUpdate) {
          const counsellorMsgs = data.counselor_message?.system_messages || [];
          const clientMsgs = data.smoker_response?.system_messages || [];
          onSystemMessagesUpdate(
            counsellorMsgs,
            clientMsgs,
            data.stage_metrics || null,
            data.working_alliance_series || [],
            data.all_tasks_series || [],
            {
              automitiLabels: data.automiti_labels || [],
              evaluation: data.evaluation || null,
              traineeText: data.counselor_message?.text || messageToSend,
            }
          );
        }
        if (onRevealedItemsUpdate && data.revealed_items) {
          onRevealedItemsUpdate(data.revealed_items);
        }
        if (onStageMetricsUpdate && data.stage_metrics) {
          onStageMetricsUpdate(data.stage_metrics);
        }
        if (data.session_ended) {
          setSessionEnded(true);
          setEndReason(data.end_reason || 'The client has ended the session');
          onSessionEnd?.(sessionId);
        }
        setLoading(false);
      },
      (errorMsg) => {
        console.error('Stream error:', errorMsg);
        notify('Failed to send message. Please try again.');
        setConversation((prev) => prev.filter((message) => !message.isStreaming));
        setLoading(false);
      },
      {
        onClassification: (data) => onTurnClassification?.(data.automiti_labels || []),
        onGuidance: (feedback) => onTurnGuidance?.(feedback),
        onGuidanceError: (message) => onTurnGuidanceError?.(message),
      }
    );

    await promise;
  }, [
    conversationLength,
    notify,
    onConversationStart,
    onRevealedItemsUpdate,
    onSessionEnd,
    onStageMetricsUpdate,
    onSystemMessagesUpdate,
    onTurnClassification,
    onTurnGuidance,
    onTurnGuidanceError,
    sessionId,
    setConversation,
    setEndReason,
    setLoading,
    setSessionEnded,
  ]);

  return {
    resolveClientType,
    initSession,
    handleFreeformSend,
  };
}

export default useChatSession;
